import { useState, useCallback, useEffect, useRef } from "react";
import { Grid, Box, Typography, Divider, IconButton, Menu, MenuItem, CircularProgress, Paper } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import {
  TextSearch,
  TertiaryButton,
  InlineButton,
  Body1,
  NotFoundPage,
  blobFromRequest,
  downloadUrl,
} from "@coherehealth/common";
import { User, useDownloadUsersAsCsv, useBatchUpdateUserInfo, UserManagementResponse } from "@coherehealth/qm-api";
import { useSnackbar } from "notistack";
import { error as logError } from "logger";
import PageHeader from "../common/PageHeader";
import { useAuthorized } from "Authorization";
import UserManagementTable from "./UserManagementTable";
import EditUserDrawer from "./EditUserDrawer";
import { UploadCompleteDrawer } from "./UploadCompleteDrawer";

const MAX_FILE_SIZE = 104857600;

// Compact padding to match Figma design spec (VQA note #3, Figma node 8286-43982)
const compactButtonStyle = { paddingTop: 4, paddingBottom: 4, paddingLeft: 12, paddingRight: 12 };

export default function UserManagementV2() {
  const isQMManager = useAuthorized("QM_MANAGER");
  const { enqueueSnackbar } = useSnackbar();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [userCount, setUserCount] = useState<number>(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"edit" | "add">("edit");
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const refetchTableRef = useRef<(() => void) | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastUploadedFileNameRef = useRef<string>();

  // CSV download/upload hooks (reused from V1 UserManagement)
  const { refetch: downloadUsersAsCsv } = useDownloadUsersAsCsv({ lazy: true });
  const { mutate: uploadUserCsv, loading: uploadingUserCsv, error: errorUploadingUserCsv } = useBatchUpdateUserInfo({});
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [uploadUserCsvResponse, setUploadUserCsvResponse] = useState<UserManagementResponse | null>();
  const [openResultsDrawer, setOpenResultsDrawer] = useState<boolean>(false);

  // Debounce search input by 300ms before hitting the API
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // CSV export: download user list as CSV
  const handleExportUserList = useCallback(async () => {
    setMenuAnchorEl(null);
    try {
      const blob = await blobFromRequest(downloadUsersAsCsv({}));
      if (blob?.type.startsWith("text/csv")) {
        const url = URL.createObjectURL(blob);
        downloadUrl(url, "users.csv");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        enqueueSnackbar("Error downloading user list: unexpected response format", { variant: "error" });
      }
    } catch {
      enqueueSnackbar("Error downloading user list", { variant: "error" });
    }
  }, [downloadUsersAsCsv, enqueueSnackbar]);

  // CSV import: trigger hidden file input
  const handleUploadViaCsv = useCallback(() => {
    setMenuAnchorEl(null);
    fileInputRef.current?.click();
  }, []);

  // Handle file selection — only stages the file, does NOT upload yet
  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset so the same file can be re-selected
      e.target.value = "";
      if (!file) {
        return;
      }

      // instanceof File check removed — FileList API guarantees File objects
      if (file.size > MAX_FILE_SIZE) {
        logError(`Selected file "${file.name}" is too large (${file.size} bytes, max allowed ${MAX_FILE_SIZE}) bytes`);
        enqueueSnackbar(`Selected file "${file.name}" is too large`, { variant: "error" });
        return;
      }

      setSelectedFile(file);
    },
    [enqueueSnackbar]
  );

  // Explicit upload — only fires when user confirms.
  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }
    // Reset previous upload state before starting a new one
    setUploadUserCsvResponse(null);
    setOpenResultsDrawer(false);
    lastUploadedFileNameRef.current = selectedFile.name;
    const formData = new FormData();
    formData.set("file", selectedFile);
    try {
      // restful-react types the body as void because the OpenAPI spec doesn't describe
      // multipart/form-data; the cast is intentional — FormData is passed through at runtime.
      const response = await uploadUserCsv(formData as unknown as void);
      setUploadUserCsvResponse(response);
      setOpenResultsDrawer(true);
      setSelectedFile(undefined);
      refetchTableRef.current?.();
    } catch {
      // HTTP errors are surfaced via the errorUploadingUserCsv useEffect (snackbar only).
      // Do not open the drawer here to avoid showing the error twice (drawer + snackbar).
      setSelectedFile(undefined);
    }
  };

  // Show upload errors as snackbar and clear staged file
  useEffect(() => {
    if (errorUploadingUserCsv) {
      const error: any = errorUploadingUserCsv;
      const message = error?.data?.message;
      const embeddedError = error?.data?._embedded?.errors?.map((err: any) => err?.message).join("\n");
      const errorMessage = (
        <Box>
          <Box>Error uploading/processing csv</Box>
          {embeddedError ? <Box>{embeddedError}</Box> : message && <Box>{message}</Box>}
        </Box>
      );
      enqueueSnackbar(errorMessage, { variant: "error" });
      logError(`Error uploading/processing csv file="${lastUploadedFileNameRef.current}" ${message} ${embeddedError}`);
    }
  }, [errorUploadingUserCsv, enqueueSnackbar]);

  const handleAddUser = useCallback(() => {
    setSelectedUserId(undefined);
    setDrawerMode("add");
    setDrawerOpen(true);
  }, []);

  const handleEditUser = useCallback((user: User) => {
    setSelectedUserId(user.id);
    setDrawerMode("edit");
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  const handleUserSaved = useCallback(() => {
    setDrawerOpen(false);
    // Refetch the grid in place (preserves pagination/sort/selection) rather than remounting it.
    refetchTableRef.current?.();
  }, []);

  const handleRefetchReady = useCallback((refetch: () => void) => {
    refetchTableRef.current = refetch;
  }, []);

  if (!isQMManager) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Grid
        container
        sx={{ padding: 4, flexDirection: "column", overflow: "hidden" }}
        data-testid="user-management-v2-container"
      >
        <Box>
          <PageHeader headerLabel="User management" hideBottomLine />
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Manage team members and update details for queue mapping
        </Typography>

        <Grid container sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", mt: 6, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextSearch
              value={searchQuery}
              onChangeValue={setSearchQuery}
              placeholder="Search users"
              inputProps={{ "aria-label": "Search users" }}
              style={{ width: 200 }}
            />
            <TertiaryButton startIcon={<FilterAltIcon />} style={compactButtonStyle} disabled>
              Filter
            </TertiaryButton>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TertiaryButton
              startIcon={<AddCircleOutlineIcon />}
              data-testid="user-management-add-user-btn"
              style={compactButtonStyle}
              onClick={handleAddUser}
            >
              Add new user
            </TertiaryButton>
            <IconButton
              size="small"
              aria-label="More actions"
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              data-testid="user-management-overflow-btn"
              sx={{ color: "primary.main", width: 24, height: 24, aspectRatio: "1/1" }}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
              <MenuItem onClick={handleUploadViaCsv} disabled={uploadingUserCsv}>
                {uploadingUserCsv ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Uploading...
                  </>
                ) : (
                  "Upload via .csv"
                )}
              </MenuItem>
              <MenuItem onClick={handleExportUserList}>Export user list</MenuItem>
            </Menu>
            {/* Hidden file input for CSV upload */}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".csv"
              onChange={onFileChange}
              data-testid="user-management-csv-input"
            />
          </Box>
        </Grid>

        <Divider sx={{ width: "100%" }} />

        {selectedFile && (
          <Paper
            elevation={1}
            sx={{ mt: 2, px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid="csv-upload-confirmation"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Body1>
                Upload <b>{selectedFile.name}</b> file to update users' info
              </Body1>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {uploadingUserCsv ? (
                <CircularProgress size={24} />
              ) : (
                <InlineButton onClick={handleUpload} data-testid="csv-upload-confirm-btn">
                  Upload
                </InlineButton>
              )}
              <IconButton
                size="small"
                onClick={() => setSelectedFile(undefined)}
                aria-label="Cancel file selection"
                disabled={uploadingUserCsv}
                data-testid="csv-upload-cancel-btn"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 1 }} data-testid="user-count-label">
          {tableLoading ? "Loading users..." : tableError ? "--" : `${userCount} users`}
        </Typography>

        <UserManagementTable
          searchQuery={debouncedSearchQuery}
          onUserCountChange={setUserCount}
          onLoadingChange={setTableLoading}
          onErrorChange={setTableError}
          onEditUser={handleEditUser}
          onRefetchReady={handleRefetchReady}
        />

        <Box data-testid="user-management-drawer-slot">
          <EditUserDrawer
            open={drawerOpen}
            mode={drawerMode}
            userId={selectedUserId}
            onClose={handleDrawerClose}
            onSaved={handleUserSaved}
          />
        </Box>
      </Grid>
      <UploadCompleteDrawer
        open={openResultsDrawer}
        handleClose={() => setOpenResultsDrawer(false)}
        userManagementResponse={uploadUserCsvResponse ?? {}}
        transactionError={uploadingUserCsv ? null : errorUploadingUserCsv}
      />
    </>
  );
}
